import { useEffect, useRef } from 'react';

/**
 * Editor de un mensaje con VARIABLES (0.27.0). "{nombre}" escrito a mano se ve igual que texto, y nadie
 * sabe si de verdad se va a cambiar por el nombre de la paciente. Aquí cada variable es una PASTILLA que
 * no se puede partir ni escribir mal: se agrega con su botón, se borra entera, y abajo se ve el mensaje
 * como le va a llegar ("Hola María, …").
 *
 * El valor sigue siendo texto plano con {llaves} — el formato que ya lee el reloj de la app
 * (Loyaltyapp → reloj-programa.js `rellenar`) — así que nada cambia del otro lado.
 */
export interface Variable { clave: string; etiqueta: string; ejemplo: string }

const esc = (t: string) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const chip = (v: Variable) => `<span class="var-chip" contenteditable="false" data-var="${v.clave}">${esc(v.etiqueta)}</span>`;

function aHtml(texto: string, vars: Variable[]) {
  const por = new Map(vars.map((v) => [v.clave, v]));
  return texto.split(/(\{\w+\})/g).map((p) => {
    const m = /^\{(\w+)\}$/.exec(p);
    if (m && por.has(m[1])) return chip(por.get(m[1])!);
    return esc(p).replace(/\n/g, '<br>');
  }).join('');
}
function aTexto(el: HTMLElement): string {
  let out = '';
  el.childNodes.forEach((n) => {
    if (n.nodeType === Node.TEXT_NODE) out += n.textContent || '';
    else if (n instanceof HTMLElement) {
      if (n.dataset.var) out += `{${n.dataset.var}}`;
      else if (n.tagName === 'BR') out += '\n';
      else { if (n.tagName === 'DIV' && out && !out.endsWith('\n')) out += '\n'; out += aTexto(n); }
    }
  });
  return out.replace(/\u00a0/g, ' ').replace(/\u200b/gi, '');
}
export function rellenarEjemplo(texto: string, vars: Variable[]) {
  const por = new Map(vars.map((v) => [v.clave, v.ejemplo]));
  return texto.replace(/\{(\w+)\}/g, (m, k) => (por.has(k) ? por.get(k)! : m));
}

export function MensajeEditor({ valor, onChange, onCommit, variables, placeholder, max = 300, disabled, etiqueta }: {
  valor: string; onChange?: (v: string) => void; onCommit?: (v: string) => void; variables: Variable[];
  placeholder?: string; max?: number; disabled?: boolean; etiqueta?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const ultimo = useRef<string | null>(null);
  // Sólo se repinta cuando el valor cambia DESDE AFUERA: repintar en cada tecla mueve el cursor.
  useEffect(() => {
    if (!ref.current || valor === ultimo.current) return;
    ref.current.innerHTML = aHtml(valor || '', variables);
    ultimo.current = valor;
  }, [valor, variables]);
  const leer = () => { const t = aTexto(ref.current!).slice(0, max); ultimo.current = t; onChange?.(t); return t; };
  const insertar = (v: Variable) => {
    const el = ref.current; if (!el || disabled) return;
    el.focus();
    const sel = window.getSelection();
    let r = sel && sel.rangeCount ? sel.getRangeAt(0) : null;
    if (!r || !el.contains(r.commonAncestorContainer)) { r = document.createRange(); r.selectNodeContents(el); r.collapse(false); }
    const tmp = document.createElement('span'); tmp.innerHTML = chip(v);
    // Tras la pastilla va un espacio de ANCHO CERO: el cursor necesita un nodo de texto donde pararse
    // (después de un elemento no editable, Chrome no deja escribir), y no se cuela un espacio de más en
    // "Hola {nombre}, …". Al leer se quita.
    const nodo = tmp.firstChild!; const ancla = document.createTextNode('\u200B');
    r.deleteContents(); r.insertNode(ancla); r.insertNode(nodo);
    r.setStart(ancla, 1); r.collapse(true); sel?.removeAllRanges(); sel?.addRange(r);
    const t = leer(); onCommit?.(t);
  };
  const vacio = !valor;
  return (
    <div className="mensaje-editor" data-editor={etiqueta || ''} style={{ display: 'grid', gap: 6, opacity: disabled ? 0.6 : 1 }}>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', fontSize: 12 }}>
        <span className="muted">Agregar:</span>
        {variables.map((v) => (
          <button key={v.clave} type="button" className="var-btn" disabled={disabled} onMouseDown={(e) => e.preventDefault()} onClick={() => insertar(v)} data-insertar={v.clave}>+ {v.etiqueta}</button>
        ))}
      </div>
      <div className="mensaje-caja">
        <div ref={ref} className="mensaje-campo" contentEditable={!disabled} suppressContentEditableWarning role="textbox" aria-label={etiqueta || 'Mensaje'} aria-multiline="true"
          onInput={leer} onBlur={() => onCommit?.(leer())}
          onPaste={(e) => { e.preventDefault(); document.execCommand('insertText', false, e.clipboardData.getData('text/plain')); }} />
        {vacio && placeholder && <div className="mensaje-ph" aria-hidden="true">{placeholder}</div>}
      </div>
      {!vacio && (
        <div className="mensaje-vista" data-vista>
          <span className="muted">Así le llega: </span>{rellenarEjemplo(valor, variables)}
        </div>
      )}
    </div>
  );
}
